/**
 * CatalogPromotionUI.tsx
 * Light enterprise theme — exact design tokens from GovernanceUI.
 *
 * NEW in this version:
 *  · Collibra Community→Domain tree picker in StepMapping (replaces raw UUID inputs)
 *  · FETCH_STRUCTURE call fires after successful test connection (Step 1)
 *  · "Enter manually" escape hatch still available (collapsed by default)
 *  · Activity Log panel in modal footer — collapsible, timestamped, colour-coded
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { adriva_catalog_postAction } from "../API";

// ─── DESIGN TOKENS — exact copy from GovernanceUI ────────────────────────────
const T = {
  bg:          "#eef1f6",
  bgCard:      "#ffffff",
  bgSidebar:   "#f5f7fa",
  bgPanel:     "#f5f7fa",
  bgInput:     "#ffffff",
  textPrimary: "#0f1923",
  textSecond:  "#1e2d3d",
  textMuted:   "#445566",
  textLabel:   "#2c3e52",
  brand:       "#1e3a6e",
  brandLight:  "#dde7f5",
  brandMid:    "#90aed0",
  brandText:   "#1e3a6e",
  border:      "#c8d4e0",
  borderFocus: "#1e3a6e",
  green:       "#166534",
  greenBg:     "#f0fdf4",
  greenBorder: "#86efac",
  amber:       "#7c2d00",
  amberBg:     "#fff7ed",
  amberBorder: "#fde68a",
  red:         "#991b1b",
  redBg:       "#fef2f2",
  redBorder:   "#fca5a5",
};

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface Entity        { entityKey: string; entityName: string; }
interface Creds         { host: string; username?: string; password?: string; api_token?: string; auth_type: "basic" | "token"; }
interface EntityMapping {
  asset_type_map: Record<string, string>;
  attribute_map:  Record<string, string>;
  domain_id?: string; community_id?: string;
  schema_id?: string; table_name?: string; article_template_id?: number;
}
interface DiffRow { field: string; attribute: string; adriva_value: any; catalog_value: any; change_type: "new" | "update" | "no_change"; }

interface CollibraDomain    { id: string; name: string; type: string; description: string; }
interface CollibraCommunity { id: string; name: string; description: string; domains: CollibraDomain[]; }

interface LogEntry {
  id:        number;
  ts:        string;
  level:     "info" | "ok" | "warn" | "error" | "debug";
  action:    string;
  target?:   string;
  entity?:   string;
  msg:       string;
}

interface CatalogPromotionUIProps {
  entities:       Entity[];
  appNm:          string;
  entityId:       string;
  savedCreds?:    Record<string, Creds>;
  savedMappings?: Record<string, Record<string, EntityMapping>>;
  onClose:        () => void;
}

// ─── DEFAULTS ─────────────────────────────────────────────────────────────────
const COLLIBRA_DEFAULTS: EntityMapping = {
  asset_type_map: {
    column:                     "00000000-0000-0000-0000-000000031007",
    business_term:              "00000000-0000-0000-0000-000000011001",
    policy:                     "00000000-0000-0000-0000-000000020003",
    has_business_term_relation: "00000000-0000-0000-0000-000000007060",
    governed_by_relation:       "00000000-0000-0000-0000-000000007086",
  },
  attribute_map: {
    sensitivity:"SENSITIVITY_CUSTOM",privacy_category:"PRIVACY_CAT_CUSTOM",
    regulatory_frameworks:"REG_FRAMEWORKS_CUSTOM",ownership:"OWNERSHIP_CUSTOM",
    retention_policy:"RETENTION_POLICY_CUSTOM",masking_required:"MASKING_REQUIRED_CUSTOM",
    encryption_required:"ENCRYPTION_REQUIRED_CUSTOM",access_control:"ACCESS_CONTROL_CUSTOM",
    anonymization_recommendation:"ANON_RECOMMENDATION_CUSTOM",anonymization_required:"ANON_REQUIRED_CUSTOM",
    anonymization_methods:"ANON_METHODS_CUSTOM",anonymization_score:"ANON_SCORE_CUSTOM",
    is_cde:"IS_CDE_CUSTOM",cde_tier:"CDE_TIER_CUSTOM",cde_score:"CDE_SCORE_CUSTOM",
    cde_confidence:"CDE_CONFIDENCE_CUSTOM",cde_rationale:"CDE_RATIONALE_CUSTOM",
    criticality:"CRITICALITY_CUSTOM",steward_role:"STEWARD_ROLE_CUSTOM",
    usage_contexts:"USAGE_CONTEXTS_CUSTOM",lifecycle:"LIFECYCLE_CUSTOM",
    dq_dimensions:"DQ_DIMENSIONS_CUSTOM",contains_personal_data:"CONTAINS_PD_CUSTOM",
    taxonomy_path:"TAXONOMY_PATH_CUSTOM",notes:"NOTES_CUSTOM",
  },
  domain_id: "", community_id: "",
};

const ALATION_DEFAULTS: EntityMapping = {
  asset_type_map: {},
  attribute_map: {
    sensitivity:"Data Sensitivity",privacy_category:"Privacy Category",
    regulatory_frameworks:"Regulatory Frameworks",ownership:"Data Ownership",
    retention_policy:"Retention Policy",masking_required:"Masking Required",
    encryption_required:"Encryption Required",access_control:"Access Control",
    anonymization_recommendation:"Anonymization Recommendation",
    anonymization_required:"Anonymization Required",anonymization_methods:"Anonymization Methods",
    anonymization_score:"Anonymization Risk Score",is_cde:"Is Critical Data Element",
    cde_tier:"CDE Tier",cde_score:"CDE Score",cde_rationale:"CDE Rationale",
    criticality:"Business Criticality",steward_role:"Data Steward Role",
    usage_contexts:"Usage Contexts",lifecycle:"Data Lifecycle",
    dq_dimensions:"DQ Dimensions",contains_personal_data:"Contains Personal Data",
    taxonomy_path:"Data Taxonomy Path",notes:"Governance Notes",
  },
  schema_id: "", table_name: "", article_template_id: 1,
};

const COLLIBRA_ASSET_ROWS = [
  { k:"column",                     l:"Column asset type UUID" },
  { k:"business_term",              l:"Business Term asset type UUID" },
  { k:"policy",                     l:"Policy asset type UUID" },
  { k:"has_business_term_relation", l:"'has Business Term' relation type UUID" },
  { k:"governed_by_relation",       l:"'governed by' relation type UUID" },
];

const ATTR_ROWS = [
  { k:"sensitivity",                  l:"Sensitivity" },
  { k:"privacy_category",             l:"Privacy Category" },
  { k:"regulatory_frameworks",        l:"Regulatory Frameworks" },
  { k:"ownership",                    l:"Data Ownership" },
  { k:"retention_policy",             l:"Retention Policy" },
  { k:"masking_required",             l:"Masking Required" },
  { k:"encryption_required",          l:"Encryption Required" },
  { k:"access_control",               l:"Access Control" },
  { k:"anonymization_recommendation", l:"Anonymization Recommendation" },
  { k:"anonymization_required",       l:"Anonymization Required" },
  { k:"anonymization_methods",        l:"Anonymization Methods" },
  { k:"anonymization_score",          l:"Risk Score" },
  { k:"is_cde",                       l:"Is CDE" },
  { k:"cde_tier",                     l:"CDE Tier" },
  { k:"cde_score",                    l:"CDE Score" },
  { k:"cde_rationale",                l:"CDE Rationale" },
  { k:"criticality",                  l:"Business Criticality" },
  { k:"steward_role",                 l:"Steward Role" },
  { k:"usage_contexts",               l:"Usage Contexts" },
  { k:"lifecycle",                    l:"Lifecycle" },
  { k:"dq_dimensions",                l:"DQ Dimensions" },
  { k:"contains_personal_data",       l:"Contains Personal Data" },
  { k:"taxonomy_path",                l:"Taxonomy Path" },
  { k:"notes",                        l:"Notes" },
];

// ─── LOG HELPERS ──────────────────────────────────────────────────────────────
let _logId = 0;
function mkLog(
  level: LogEntry["level"],
  action: string,
  msg: string,
  target?: string,
  entity?: string,
): LogEntry {
  const now = new Date();
  const ts  = `${now.getHours().toString().padStart(2,"0")}:${now.getMinutes().toString().padStart(2,"0")}:${now.getSeconds().toString().padStart(2,"0")}.${now.getMilliseconds().toString().padStart(3,"0")}`;
  return { id: ++_logId, ts, level, action, target, entity, msg };
}

const LOG_COLORS: Record<LogEntry["level"], { bg: string; border: string; color: string; dot: string }> = {
  info:  { bg: "#f0f4ff", border: "#c7d5f0", color: T.brand,   dot: T.brand },
  ok:    { bg: T.greenBg, border: T.greenBorder, color: T.green, dot: T.green },
  warn:  { bg: T.amberBg, border: T.amberBorder, color: T.amber, dot: "#d97706" },
  error: { bg: T.redBg,   border: T.redBorder,   color: T.red,   dot: T.red },
  debug: { bg: "#f8f9fb", border: T.border,       color: T.textMuted, dot: "#94a3b8" },
};

// ─── API ──────────────────────────────────────────────────────────────────────
function postCatalogAction(p: object): Promise<any> {
  return new Promise(resolve => {
    adriva_catalog_postAction(
      JSON.stringify(p),
      (res: any) => resolve(res),
      (err: any) => resolve({ success: false, msg: err?.message ?? "Network error" }),
    );
  });
}

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width:"100%", background:T.bgInput, border:`1px solid ${T.border}`,
  borderRadius:7, padding:"9px 12px", color:T.textPrimary,
  fontSize:"0.83rem", outline:"none", fontFamily:"inherit", transition:"border-color 0.15s",
};
const labelStyle: React.CSSProperties = {
  display:"block", fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
  textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6,
};
const sectionHead: React.CSSProperties = {
  fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.1em",
  textTransform:"uppercase", color:T.textMuted,
  borderBottom:`1px solid ${T.border}`, paddingBottom:8, marginBottom:16,
};
function smallBtn(color: string, muted = false): React.CSSProperties {
  return {
    background:"transparent", border:`1px solid ${muted ? T.border : color + "60"}`,
    color: muted ? T.textMuted : color,
    borderRadius:6, padding:"5px 14px", fontSize:"0.72rem", fontWeight:600, cursor:"pointer",
  };
}

function Badge({ label, color, bg, border }: { label:string; color:string; bg:string; border:string }) {
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", fontSize:"0.7rem", fontWeight:700,
      padding:"3px 10px", borderRadius:5, background:bg, color, border:`1px solid ${border}`,
    }}>{label}</span>
  );
}

function PrimaryBtn({
  label, onClick, disabled, variant = "brand",
}: {
  label:string; onClick:()=>void; disabled?:boolean; variant?:"brand"|"green"|"amber";
}) {
  const colors = {
    brand:  { bg:T.brand,   border:T.brand },
    green:  { bg:"#166534", border:"#166534" },
    amber:  { bg:"#92400e", border:"#92400e" },
  };
  const c = colors[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? T.border : c.bg,
      border:`1px solid ${disabled ? T.border : c.border}`,
      color:"#fff", borderRadius:7, padding:"7px 18px",
      fontSize:"0.78rem", fontWeight:700,
      cursor: disabled ? "default" : "pointer",
      boxShadow: disabled ? "none" : "0 2px 8px rgba(44,74,124,0.18)",
      transition:"all 0.15s",
    }}>{label}</button>
  );
}

// ─── STEP BAR ──────────────────────────────────────────────────────────────────
function StepBar({ step }: { step: number }) {
  const steps = ["Credentials", "Mapping", "Diff Preview", "Push"];
  return (
    <div style={{
      display:"flex", alignItems:"center",
      background:T.bgSidebar, border:`1px solid ${T.border}`,
      borderRadius:10, padding:"14px 24px", marginBottom:24, gap:0,
    }}>
      {steps.map((s, i) => {
        const done=i<step, active=i===step, last=i===steps.length-1;
        return (
          <React.Fragment key={s}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{
                width:28, height:28, borderRadius:"50%", flexShrink:0,
                background: done ? T.green : active ? T.brand : T.bgCard,
                border:`2px solid ${done ? T.green : active ? T.brand : T.border}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"0.72rem", fontWeight:800,
                color: done||active ? "#fff" : T.textMuted, transition:"all 0.25s",
              }}>{done ? "✓" : i+1}</div>
              <div>
                <div style={{
                  fontSize:"0.72rem", fontWeight:active?700:500,
                  color: active?T.brand : done?T.green : T.textMuted, whiteSpace:"nowrap",
                }}>{s}</div>
                {active && <div style={{ fontSize:"0.6rem", color:T.textMuted, marginTop:1 }}>In progress</div>}
              </div>
            </div>
            {!last && (
              <div style={{
                flex:1, height:2, margin:"0 16px",
                background: done ? T.green : T.border, transition:"background 0.3s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── COLLIBRA STRUCTURE TREE PICKER ───────────────────────────────────────────
function CollibraTreePicker({
  communities,
  selectedDomainId,
  selectedCommunityId,
  onSelect,
}: {
  communities:         CollibraCommunity[];
  selectedDomainId:    string;
  selectedCommunityId: string;
  onSelect:            (community: CollibraCommunity, domain: CollibraDomain) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(
    // Auto-expand the community that contains the currently selected domain
    communities
      .filter(c => c.domains.some(d => d.id === selectedDomainId))
      .map(c => c.id)
  ));

  const toggle = (cid: string) =>
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(cid) ? s.delete(cid) : s.add(cid);
      return s;
    });

  // Group type badge colours
  const typeColor = (type: string): string => {
    const t = (type || "").toLowerCase();
    if (t.includes("glossary"))     return T.green;
    if (t.includes("data"))         return T.brand;
    if (t.includes("policy"))       return "#92400e";
    if (t.includes("catalog"))      return "#6b21a8";
    return T.textMuted;
  };

  return (
    <div style={{
      border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden",
      background:T.bgCard, boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
    }}>
      {/* Header */}
      <div style={{
        padding:"10px 16px", background:T.bgSidebar,
        borderBottom:`1px solid ${T.border}`,
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <span style={{ fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
          textTransform:"uppercase", letterSpacing:"0.08em" }}>
          Collibra Workspace
        </span>
        {selectedDomainId && (
          <span style={{
            fontSize:"0.68rem", background:T.greenBg, border:`1px solid ${T.greenBorder}`,
            color:T.green, borderRadius:4, padding:"2px 8px", fontWeight:700,
          }}>✓ Domain selected</span>
        )}
      </div>

      {/* Tree body */}
      <div style={{ maxHeight:340, overflowY:"auto" }}>
        {communities.length === 0 ? (
          <div style={{ padding:"24px", textAlign:"center", color:T.textMuted,
            fontSize:"0.82rem", fontStyle:"italic" }}>
            No communities found
          </div>
        ) : communities.map(comm => {
          const open      = expanded.has(comm.id);
          const hasActive = comm.domains.some(d => d.id === selectedDomainId);
          return (
            <div key={comm.id}>
              {/* Community row */}
              <div
                onClick={() => toggle(comm.id)}
                style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding:"9px 16px", cursor:"pointer",
                  background: hasActive ? T.brandLight : T.bgCard,
                  borderBottom:`1px solid ${T.border}`,
                  transition:"background 0.12s",
                  userSelect:"none",
                }}
              >
                <span style={{
                  fontSize:"0.7rem", color:open ? T.brand : T.textMuted,
                  transition:"transform 0.15s", display:"inline-block",
                  transform: open ? "rotate(90deg)" : "none",
                }}>▶</span>
                <span style={{ fontSize:"1rem" }}>🏛</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{
                    fontSize:"0.82rem", fontWeight: hasActive ? 700 : 600,
                    color: hasActive ? T.brand : T.textPrimary,
                    fontFamily:"'Lora',Georgia,serif",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  }}>{comm.name}</div>
                  {comm.description && (
                    <div style={{ fontSize:"0.62rem", color:T.textMuted,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      marginTop:1, maxWidth:300 }}>
                      {comm.description}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize:"0.62rem", color:T.textMuted,
                  background:T.bgSidebar, border:`1px solid ${T.border}`,
                  borderRadius:10, padding:"1px 8px",
                }}>
                  {comm.domains.length} domain{comm.domains.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Domain rows */}
              {open && comm.domains.map((dom, i) => {
                const selected = dom.id === selectedDomainId;
                return (
                  <div
                    key={dom.id}
                    onClick={() => onSelect(comm, dom)}
                    style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"8px 16px 8px 44px", cursor:"pointer",
                      background: selected ? T.brandLight : i%2===0 ? "#fafbfd" : T.bgCard,
                      borderBottom:`1px solid ${T.border}`,
                      borderLeft: selected ? `3px solid ${T.brand}` : "3px solid transparent",
                      transition:"all 0.1s",
                    }}
                  >
                    <span style={{ fontSize:"0.88rem" }}>
                      {dom.type?.toLowerCase().includes("glossary") ? "📖"
                        : dom.type?.toLowerCase().includes("policy") ? "📋"
                        : dom.type?.toLowerCase().includes("catalog") ? "🗂"
                        : "📁"}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:"0.8rem", fontWeight: selected ? 700 : 500,
                        color: selected ? T.brand : T.textPrimary,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      }}>{dom.name}</div>
                      {dom.description && (
                        <div style={{ fontSize:"0.6rem", color:T.textMuted,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {dom.description}
                        </div>
                      )}
                    </div>
                    {dom.type && (
                      <span style={{
                        fontSize:"0.58rem", fontWeight:700,
                        color: typeColor(dom.type),
                        background: typeColor(dom.type) + "18",
                        border:`1px solid ${typeColor(dom.type)}40`,
                        borderRadius:4, padding:"1px 7px",
                        textTransform:"uppercase", letterSpacing:"0.06em",
                        flexShrink:0,
                      }}>{dom.type}</span>
                    )}
                    {selected && (
                      <span style={{ color:T.green, fontSize:"0.9rem", flexShrink:0 }}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ACTIVITY LOG PANEL ───────────────────────────────────────────────────────
function ActivityLog({ entries, isOpen, onToggle }: {
  entries: LogEntry[];
  isOpen:  boolean;
  onToggle: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, isOpen]);

  const unread = entries.filter(e => e.level === "error" || e.level === "warn").length;

  return (
    <div style={{
      borderTop:`1px solid ${T.border}`,
      background:T.bgSidebar,
      flexShrink:0,
    }}>
      {/* Toggle bar */}
      <div
        onClick={onToggle}
        style={{
          display:"flex", alignItems:"center", gap:8,
          padding:"8px 24px", cursor:"pointer",
          userSelect:"none",
        }}
      >
        <span style={{
          fontSize:"0.68rem", transition:"transform 0.15s", display:"inline-block",
          transform: isOpen ? "rotate(90deg)" : "none", color:T.textMuted,
        }}>▶</span>
        <span style={{ fontSize:"0.7rem", fontWeight:700, color:T.textMuted,
          textTransform:"uppercase", letterSpacing:"0.08em" }}>
          Activity Log
        </span>
        <span style={{
          fontSize:"0.62rem", background:T.bgCard, border:`1px solid ${T.border}`,
          borderRadius:10, padding:"1px 8px", color:T.textMuted, fontWeight:600,
        }}>{entries.length}</span>
        {unread > 0 && (
          <span style={{
            fontSize:"0.62rem", background:T.redBg, border:`1px solid ${T.redBorder}`,
            color:T.red, borderRadius:10, padding:"1px 8px", fontWeight:700,
          }}>⚠ {unread}</span>
        )}
        <span style={{
          marginLeft:"auto", fontSize:"0.65rem", color:T.textMuted,
          fontStyle:"italic",
        }}>
          {isOpen ? "Click to collapse" : "Click to expand"}
        </span>
      </div>

      {/* Log entries */}
      {isOpen && (
        <div
          ref={scrollRef}
          style={{
            maxHeight:180, overflowY:"auto",
            borderTop:`1px solid ${T.border}`,
            fontFamily:"'SF Mono','Consolas','Fira Mono',monospace",
          }}
        >
          {entries.length === 0 ? (
            <div style={{ padding:"16px 24px", color:T.textMuted,
              fontSize:"0.72rem", fontStyle:"italic" }}>
              No activity yet — log entries will appear as you interact with the catalog.
            </div>
          ) : (
            entries.map(e => {
              const c = LOG_COLORS[e.level];
              return (
                <div
                  key={e.id}
                  style={{
                    display:"flex", alignItems:"flex-start", gap:10,
                    padding:"5px 20px",
                    background: c.bg,
                    borderBottom:`1px solid ${T.border}`,
                    borderLeft:`3px solid ${c.border}`,
                  }}
                >
                  {/* Dot */}
                  <div style={{
                    width:6, height:6, borderRadius:"50%",
                    background:c.dot, flexShrink:0, marginTop:5,
                  }} />
                  {/* Timestamp */}
                  <span style={{
                    fontSize:"0.62rem", color:T.textMuted,
                    whiteSpace:"nowrap", flexShrink:0, marginTop:1,
                  }}>{e.ts}</span>
                  {/* Level badge */}
                  <span style={{
                    fontSize:"0.58rem", fontWeight:700, padding:"1px 5px",
                    borderRadius:3, background:c.border + "40",
                    color:c.color, textTransform:"uppercase", flexShrink:0, marginTop:1,
                  }}>{e.level}</span>
                  {/* Action */}
                  {e.action && (
                    <span style={{
                      fontSize:"0.62rem", color:T.brand, fontWeight:700,
                      flexShrink:0, marginTop:1,
                    }}>[{e.action}]</span>
                  )}
                  {/* Target + entity */}
                  {(e.target || e.entity) && (
                    <span style={{
                      fontSize:"0.62rem", color:T.textMuted, flexShrink:0, marginTop:1,
                    }}>
                      {[e.target, e.entity].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {/* Message */}
                  <span style={{
                    fontSize:"0.7rem", color:c.color, lineHeight:1.5,
                    wordBreak:"break-word", flex:1,
                  }}>{e.msg}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── STEP 1: CREDENTIALS ─────────────────────────────────────────────────────
function StepCredentials({
  target, setTarget, creds, setCreds,
  testState, testMsg, structureState,
  onTest, onNext,
}: any) {
  const isColl = target === "collibra";
  return (
    <div>
      <div style={sectionHead}>Select Catalog Platform</div>
      <div style={{ display:"flex", gap:10, marginBottom:28 }}>
        {(["collibra", "alation"] as const).map(t => {
          const act    = target === t;
          const accent = t === "collibra" ? T.brand : T.green;
          return (
            <button key={t} onClick={() => setTarget(t)} style={{
              flex:1, padding:"16px 20px", borderRadius:10, cursor:"pointer",
              background:   act ? T.brandLight : T.bgCard,
              border:       `2px solid ${act ? accent : T.border}`,
              borderTop:    act ? `3px solid ${accent}` : "3px solid transparent",
              color:        act ? accent : T.textMuted,
              transition:   "all 0.15s",
              display:"flex", flexDirection:"column", alignItems:"center", gap:8,
              boxShadow:    act ? "0 2px 8px rgba(44,74,124,0.10)" : "none",
            }}>
              <span style={{ fontSize:"1.5rem" }}>{t==="collibra" ? "⬡" : "◈"}</span>
              <span style={{
                fontSize:"0.88rem", fontWeight:700, color:act ? accent : T.textSecond,
                fontFamily:"'Lora',Georgia,serif",
              }}>
                {t==="collibra" ? "Collibra DGC" : "Alation"}
              </span>
              <span style={{ fontSize:"0.68rem", color:T.textMuted, textAlign:"center", lineHeight:1.4 }}>
                {t==="collibra"
                  ? "Assets · Attributes · Relations · Policies"
                  : "Columns · Articles · Trust Flags"}
              </span>
            </button>
          );
        })}
      </div>

      <div style={sectionHead}>Connection Details</div>
      <div style={{
        background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:10,
        padding:"20px 22px", marginBottom:20,
        boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display:"grid", gap:16 }}>
          <div>
            <label style={labelStyle}>Host URL</label>
            <input style={inputStyle} value={creds.host}
              onChange={(e:any) => setCreds({ ...creds, host:e.target.value })}
              placeholder={isColl ? "https://yourorg.collibra.com" : "https://yourorg.alationcloud.com"} />
          </div>

          {!isColl && (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <span style={{ ...labelStyle, margin:0, marginRight:8 }}>Auth type</span>
              {(["basic","token"] as const).map(at => (
                <button key={at} onClick={() => setCreds({ ...creds, auth_type:at })} style={{
                  padding:"5px 14px", borderRadius:6, cursor:"pointer",
                  background: creds.auth_type===at ? T.brandLight : "transparent",
                  border:`1px solid ${creds.auth_type===at ? T.brandMid : T.border}`,
                  color: creds.auth_type===at ? T.brand : T.textMuted,
                  fontSize:"0.76rem", fontWeight:creds.auth_type===at ? 700 : 400,
                  transition:"all 0.12s",
                }}>
                  {at==="basic" ? "Username / Password" : "API Token"}
                </button>
              ))}
            </div>
          )}

          {(isColl || creds.auth_type==="basic") ? (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={labelStyle}>Username</label>
                <input style={inputStyle} value={creds.username ?? ""} autoComplete="off"
                  onChange={(e:any) => setCreds({ ...creds, username:e.target.value })}
                  placeholder="admin@yourorg.com" />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" value={creds.password ?? ""} autoComplete="off"
                  onChange={(e:any) => setCreds({ ...creds, password:e.target.value })} />
              </div>
            </div>
          ) : (
            <div>
              <label style={labelStyle}>API Token</label>
              <input style={inputStyle} type="password" value={creds.api_token ?? ""}
                onChange={(e:any) => setCreds({ ...creds, api_token:e.target.value })}
                placeholder="Paste your Alation API token" />
            </div>
          )}
        </div>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24, flexWrap:"wrap" }}>
        <PrimaryBtn
          label={testState==="testing" ? "Testing…" : "Test Connection"}
          onClick={onTest} disabled={testState==="testing"} />

        {testState==="ok" && (
          <div style={{
            display:"flex", alignItems:"center", gap:6,
            background:T.greenBg, border:`1px solid ${T.greenBorder}`,
            borderRadius:6, padding:"5px 12px",
          }}>
            <span style={{ color:T.green, fontSize:"0.82rem", fontWeight:700 }}>
              ✓ Connected
            </span>
          </div>
        )}
        {testState==="error" && (
          <div style={{ background:T.redBg, border:`1px solid ${T.redBorder}`,
            borderRadius:6, padding:"5px 12px" }}>
            <span style={{ color:T.red, fontSize:"0.8rem" }}>{testMsg || "Connection failed"}</span>
          </div>
        )}

        {/* Structure fetch status (Collibra only) */}
        {isColl && testState==="ok" && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {structureState==="loading" && (
              <span style={{ fontSize:"0.72rem", color:T.brand, fontStyle:"italic" }}>
                ⏳ Loading workspace structure…
              </span>
            )}
            {structureState==="ok" && (
              <span style={{ fontSize:"0.72rem", color:T.green, fontWeight:600 }}>
                ✓ Workspace structure loaded
              </span>
            )}
            {structureState==="error" && (
              <span style={{ fontSize:"0.72rem", color:T.amber }}>
                ⚠ Could not load workspace — you can enter domain UUIDs manually in Step 2
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <PrimaryBtn label="Next: Asset Mapping →" onClick={onNext} disabled={testState!=="ok"} />
      </div>
    </div>
  );
}

// ─── STEP 2: MAPPING ─────────────────────────────────────────────────────────
function StepMapping({
  target, entities, mappings, setMappings,
  onSave, saveState, onNext, onBack,
  collibraStructure, structureState,
}: any) {
  const [ae,          setAe]          = useState(entities[0]?.entityKey ?? "");
  const [manualMode,  setManualMode]  = useState(false);
  const isColl  = target === "collibra";
  const def     = isColl ? COLLIBRA_DEFAULTS : ALATION_DEFAULTS;
  const m: EntityMapping = mappings[ae] ?? def;
  const allSaved = entities.every((e: Entity) => saveState[e.entityKey]==="saved");

  const upd    = (p: Partial<EntityMapping>) => setMappings({ ...mappings, [ae]: { ...m, ...p } });
  const updAt  = (k: string, v: string)      => upd({ asset_type_map: { ...m.asset_type_map, [k]:v } });
  const updAtr = (k: string, v: string)      => upd({ attribute_map:  { ...m.attribute_map,  [k]:v } });

  const selectedDom  = m.domain_id    || "";
  const selectedComm = m.community_id || "";

  const handleDomainSelect = (comm: CollibraCommunity, dom: CollibraDomain) => {
    upd({ domain_id: dom.id, community_id: comm.id });
  };

  // Find selected domain name for display
  const selectedDomainName = collibraStructure?.communities
    ?.flatMap((c: CollibraCommunity) => c.domains)
    ?.find((d: CollibraDomain) => d.id === selectedDom)
    ?.name ?? null;

  const selectedCommName = collibraStructure?.communities
    ?.find((c: CollibraCommunity) => c.id === selectedComm)
    ?.name ?? null;

  return (
    <div>
      {/* Entity tabs */}
      {entities.length > 1 && (
        <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
          {entities.map((e: Entity) => (
            <button key={e.entityKey} onClick={() => setAe(e.entityKey)} style={{
              padding:"5px 16px", borderRadius:20, cursor:"pointer",
              background: ae===e.entityKey ? T.brandLight : "transparent",
              border:`1px solid ${ae===e.entityKey ? T.brandMid : T.border}`,
              color: ae===e.entityKey ? T.brand : T.textMuted,
              fontSize:"0.76rem", fontWeight:ae===e.entityKey ? 700 : 400,
              transition:"all 0.12s",
            }}>
              {e.entityName}
              {saveState[e.entityKey]==="saved" && (
                <span style={{ marginLeft:6, color:T.green, fontWeight:700 }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── COLLIBRA: Domain picker or manual UUIDs ── */}
      {isColl && (
        <div style={{ marginBottom:20 }}>
          {/* Selected domain summary card */}
          {selectedDom && selectedDomainName && (
            <div style={{
              display:"flex", alignItems:"center", gap:12,
              padding:"10px 16px", marginBottom:12,
              background:T.greenBg, border:`1px solid ${T.greenBorder}`,
              borderRadius:8,
            }}>
              <span style={{ fontSize:"1rem" }}>📁</span>
              <div>
                <div style={{ fontSize:"0.76rem", fontWeight:700, color:T.green }}>
                  Target Domain Selected
                </div>
                <div style={{ fontSize:"0.82rem", color:T.textPrimary, fontWeight:600 }}>
                  {selectedCommName && <span style={{ color:T.textMuted }}>{selectedCommName} › </span>}
                  {selectedDomainName}
                </div>
                <div style={{ fontSize:"0.62rem", color:T.textMuted, fontFamily:"monospace", marginTop:2 }}>
                  domain_id: {selectedDom}
                </div>
              </div>
              <button
                onClick={() => upd({ domain_id:"", community_id:"" })}
                style={{
                  marginLeft:"auto", background:"none", border:`1px solid ${T.greenBorder}`,
                  color:T.green, borderRadius:5, padding:"3px 10px",
                  fontSize:"0.68rem", cursor:"pointer",
                }}
              >Clear</button>
            </div>
          )}

          {/* Tree picker */}
          {!manualMode && (
            <div>
              {structureState==="loading" ? (
                <div style={{
                  padding:"32px", textAlign:"center",
                  border:`2px dashed ${T.border}`, borderRadius:10,
                  color:T.textMuted, fontSize:"0.82rem",
                }}>
                  <div style={{ fontSize:"1.4rem", marginBottom:8 }}>⏳</div>
                  Loading Collibra workspace structure…
                </div>
              ) : structureState==="ok" && collibraStructure?.communities?.length > 0 ? (
                <CollibraTreePicker
                  communities={collibraStructure.communities}
                  selectedDomainId={selectedDom}
                  selectedCommunityId={selectedComm}
                  onSelect={handleDomainSelect}
                />
              ) : (
                <div style={{
                  padding:"24px", textAlign:"center",
                  border:`2px dashed ${T.border}`, borderRadius:10,
                  color:T.textMuted, fontSize:"0.82rem", marginBottom:8,
                }}>
                  {structureState==="error"
                    ? "Could not load workspace structure from Collibra."
                    : "Workspace structure not loaded yet."}
                </div>
              )}
              <div style={{ marginTop:8, textAlign:"right" }}>
                <button onClick={() => setManualMode(true)} style={{
                  background:"none", border:"none", color:T.brand,
                  fontSize:"0.7rem", cursor:"pointer", textDecoration:"underline",
                  fontWeight:600,
                }}>
                  Enter domain UUID manually instead →
                </button>
              </div>
            </div>
          )}

          {/* Manual override */}
          {manualMode && (
            <div style={{
              background:T.bgCard, border:`1px solid ${T.border}`,
              borderRadius:10, overflow:"hidden", marginBottom:8,
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <div style={{
                padding:"10px 20px", background:T.bgSidebar,
                borderBottom:`1px solid ${T.border}`,
                display:"flex", alignItems:"center", justifyContent:"space-between",
              }}>
                <span style={{ fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
                  textTransform:"uppercase", letterSpacing:"0.08em" }}>
                  Domain & Community (manual)
                </span>
                <button onClick={() => setManualMode(false)} style={{
                  background:"none", border:"none", color:T.brand,
                  fontSize:"0.7rem", cursor:"pointer", textDecoration:"underline",
                }}>← Use tree picker</button>
              </div>
              <div style={{ padding:"16px 20px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[{ k:"domain_id", l:"Target Domain UUID" }, { k:"community_id", l:"Target Community UUID" }]
                  .map((f:any) => (
                    <div key={f.k}>
                      <label style={labelStyle}>{f.l}</label>
                      <input style={inputStyle} value={(m as any)[f.k] ?? ""}
                        onChange={(e:any) => upd({ [f.k]:e.target.value } as any)}
                        placeholder="00000000-0000-0000-…" />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ALATION: Schema & Table ── */}
      {!isColl && (
        <div style={{
          background:T.bgCard, border:`1px solid ${T.border}`,
          borderRadius:10, overflow:"hidden", marginBottom:20,
          boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <div style={{
            padding:"10px 20px", background:T.bgSidebar, borderBottom:`1px solid ${T.border}`,
            fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
            textTransform:"uppercase", letterSpacing:"0.08em",
          }}>Schema & Table</div>
          <div style={{ padding:"16px 20px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
            {[
              { k:"schema_id",          l:"Schema ID (numeric)" },
              { k:"table_name",         l:"Table name" },
              { k:"article_template_id",l:"Article template ID" },
            ].map((f:any) => (
              <div key={f.k}>
                <label style={labelStyle}>{f.l}</label>
                <input style={inputStyle} value={(m as any)[f.k] ?? ""}
                  onChange={(e:any) => upd({ [f.k]:e.target.value } as any)}
                  placeholder="e.g. 42" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Asset type UUIDs — Collibra only */}
      {isColl && (
        <div style={{
          background:T.bgCard, border:`1px solid ${T.border}`,
          borderRadius:10, overflow:"hidden", marginBottom:20,
          boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <div style={{
            padding:"10px 20px", background:T.bgSidebar, borderBottom:`1px solid ${T.border}`,
            fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
            textTransform:"uppercase", letterSpacing:"0.08em",
          }}>Asset Type UUIDs</div>
          <div style={{ padding:"16px 20px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {COLLIBRA_ASSET_ROWS.map(f => (
              <div key={f.k}>
                <label style={labelStyle}>{f.l}</label>
                <input style={inputStyle} value={m.asset_type_map[f.k] ?? ""}
                  onChange={(e:any) => updAt(f.k, e.target.value)}
                  placeholder="00000000-0000-0000-0000-…" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attribute mapping table */}
      <div style={{
        background:T.bgCard, border:`1px solid ${T.border}`,
        borderRadius:10, overflow:"hidden", marginBottom:16,
        boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{
          padding:"10px 20px", background:T.bgSidebar, borderBottom:`1px solid ${T.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <span style={{ fontSize:"0.7rem", fontWeight:700, color:T.textLabel,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>
            Attribute Mapping
          </span>
          <span style={{ fontSize:"0.68rem", color:T.textMuted, fontStyle:"italic" }}>
            ADRIVA attribute → {isColl ? "Collibra attribute type UUID" : "Alation custom field name"}
          </span>
        </div>
        <div style={{
          display:"grid", gridTemplateColumns:"1fr 1fr",
          padding:"7px 20px", background:"#fafbfd", borderBottom:`1px solid ${T.border}`,
        }}>
          <span style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>ADRIVA Attribute</span>
          <span style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted,
            textTransform:"uppercase", letterSpacing:"0.08em" }}>
            {isColl ? "Collibra UUID" : "Alation Field Name"}
          </span>
        </div>
        <div style={{ maxHeight:280, overflowY:"auto" }}>
          {ATTR_ROWS.map((row, i) => (
            <div key={row.k} style={{
              display:"grid", gridTemplateColumns:"1fr 1fr",
              padding:"8px 20px", gap:12, alignItems:"center",
              background: i%2===0 ? T.bgCard : "#fafbfd",
              borderBottom: i < ATTR_ROWS.length-1 ? `1px solid ${T.border}` : "none",
            }}>
              <div>
                <span style={{ fontSize:"0.8rem", color:T.textPrimary, fontWeight:500 }}>{row.l}</span>
                <span style={{ fontSize:"0.65rem", color:T.textMuted, marginLeft:8,
                  fontFamily:"monospace" }}>{row.k}</span>
              </div>
              <input
                style={{ ...inputStyle, padding:"6px 10px", fontSize:"0.78rem" }}
                value={m.attribute_map[row.k] ?? ""}
                onChange={(e:any) => updAtr(row.k, e.target.value)}
                placeholder={isColl ? "XXXXXXXX-XXXX-…" : row.l}
              />
            </div>
          ))}
        </div>
      </div>

      <div style={{
        padding:"10px 14px", borderRadius:8,
        background:T.amberBg, border:`1px solid ${T.amberBorder}`,
        fontSize:"0.76rem", color:T.amber, lineHeight:1.6, marginBottom:20,
      }}>
        ⚠ Review mapping carefully. Incorrect IDs will silently overwrite the wrong catalog fields.
        Save each entity's mapping before proceeding.
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <button onClick={onBack} style={smallBtn(T.textMuted, true)}>← Back</button>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => onSave(ae)} style={{
            background:T.brandLight, border:`1px solid ${T.brandMid}`, color:T.brand,
            borderRadius:7, padding:"7px 18px", fontSize:"0.76rem", fontWeight:700, cursor:"pointer",
          }}>
            {saveState[ae]==="saving" ? "Saving…" : saveState[ae]==="saved" ? "✓ Saved" : "Save Mapping"}
          </button>
          <PrimaryBtn label="Next: Diff Preview →" onClick={onNext} disabled={!allSaved} />
        </div>
      </div>
    </div>
  );
}

// ─── STEP 3: DIFF ─────────────────────────────────────────────────────────────
function StepDiff({ entities, diffData, diffLoading, onFetch, onNext, onBack }: any) {
  const [ae,     setAe]     = useState(entities[0]?.entityKey ?? "");
  const [filter, setFilter] = useState<"all"|"new"|"update">("all");
  const data     = diffData[ae];
  const loading  = diffLoading[ae];
  const rows: DiffRow[] = data?.diff ?? [];
  const shown    = rows.filter((r: DiffRow) =>
    filter==="all" ? r.change_type!=="no_change" : r.change_type===filter
  );
  const allFetched = entities.every((e: Entity) => diffData[e.entityKey] != null);

  const cc = (t: string) => t==="new" ? T.green : t==="update" ? "#9a3412" : T.textMuted;
  const cb = (t: string) => t==="new" ? T.greenBg : t==="update" ? T.amberBg : T.bgCard;
  const cx = (t: string) => t==="new" ? T.greenBorder : t==="update" ? T.amberBorder : T.border;

  return (
    <div>
      {entities.length > 1 && (
        <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
          {entities.map((e: Entity) => {
            const d = diffData[e.entityKey];
            return (
              <button key={e.entityKey} onClick={() => setAe(e.entityKey)} style={{
                padding:"5px 16px", borderRadius:20, cursor:"pointer",
                background: ae===e.entityKey ? T.brandLight : "transparent",
                border:`1px solid ${ae===e.entityKey ? T.brandMid : T.border}`,
                color: ae===e.entityKey ? T.brand : T.textMuted,
                fontSize:"0.76rem", fontWeight: ae===e.entityKey ? 700 : 400,
                display:"flex", alignItems:"center", gap:8, transition:"all 0.12s",
              }}>
                {e.entityName}
                {d?.summary.new > 0 && (
                  <span style={{ fontSize:"0.62rem", fontWeight:700, color:T.green,
                    background:T.greenBg, border:`1px solid ${T.greenBorder}`,
                    borderRadius:4, padding:"1px 6px" }}>+{d.summary.new}</span>
                )}
                {d?.summary.update > 0 && (
                  <span style={{ fontSize:"0.62rem", fontWeight:700, color:"#9a3412",
                    background:T.amberBg, border:`1px solid ${T.amberBorder}`,
                    borderRadius:4, padding:"1px 6px" }}>~{d.summary.update}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <button onClick={() => onFetch(ae)} disabled={loading} style={{
          background:T.brandLight, border:`1px solid ${T.brandMid}`, color:T.brand,
          borderRadius:7, padding:"7px 16px", fontSize:"0.76rem", fontWeight:700,
          cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1,
        }}>
          {loading ? "Fetching…" : data ? "↻ Refresh Diff" : "Fetch Diff from Catalog"}
        </button>
        {data && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Badge label={`${data.summary.new} new`} color={T.green} bg={T.greenBg} border={T.greenBorder} />
            <Badge label={`${data.summary.update} updates`} color="#9a3412" bg={T.amberBg} border={T.amberBorder} />
            <Badge label={`${data.summary.no_change} unchanged`} color={T.textMuted} bg={T.bgSidebar} border={T.border} />
            <Badge label={`${data.summary.total_fields} fields`} color={T.brand} bg={T.brandLight} border={T.brandMid} />
          </div>
        )}
        {data && (
          <div style={{ marginLeft:"auto", display:"flex", gap:4 }}>
            {(["all","new","update"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding:"4px 12px", borderRadius:5, cursor:"pointer",
                background: filter===f ? T.brandLight : "transparent",
                border:`1px solid ${filter===f ? T.brandMid : T.border}`,
                color: filter===f ? T.brand : T.textMuted,
                fontSize:"0.7rem", fontWeight: filter===f ? 700 : 400,
              }}>
                {f==="all" ? "All changes" : f==="new" ? "New only" : "Updates only"}
              </button>
            ))}
          </div>
        )}
      </div>

      {data ? (
        <div style={{
          background:T.bgCard, border:`1px solid ${T.border}`,
          borderRadius:10, overflow:"hidden", marginBottom:20,
          boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <div style={{
            display:"grid", gridTemplateColumns:"160px 160px 1fr 1fr 70px",
            padding:"8px 16px", background:T.bgSidebar, borderBottom:`1px solid ${T.border}`,
          }}>
            {["Field","Attribute","ADRIVA Value","Catalog Current","Change"].map(h => (
              <span key={h} style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted,
                textTransform:"uppercase", letterSpacing:"0.08em" }}>{h}</span>
            ))}
          </div>
          <div style={{ maxHeight:380, overflowY:"auto" }}>
            {shown.length===0 ? (
              <div style={{ padding:"32px", textAlign:"center", color:T.textMuted,
                fontSize:"0.84rem", fontStyle:"italic" }}>
                {filter==="all" ? "No changes detected — catalog is up to date."
                  : `No "${filter}" rows for current filter.`}
              </div>
            ) : shown.map((row: DiffRow, i: number) => (
              <div key={`${row.field}_${row.attribute}_${i}`} style={{
                display:"grid", gridTemplateColumns:"160px 160px 1fr 1fr 70px",
                padding:"8px 16px", gap:8, alignItems:"center",
                background: i%2===0 ? T.bgCard : "#fafbfd",
                borderBottom:`1px solid ${T.border}`,
              }}>
                <span style={{ fontSize:"0.75rem", color:T.textPrimary, fontWeight:600,
                  fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis",
                  whiteSpace:"nowrap" }}>{row.field}</span>
                <span style={{ fontSize:"0.73rem", color:T.textMuted }}>{row.attribute}</span>
                <span style={{ fontSize:"0.73rem", color:T.green, wordBreak:"break-word",
                  lineHeight:1.4, fontWeight:500 }}>{String(row.adriva_value ?? "—")}</span>
                <span style={{
                  fontSize:"0.73rem",
                  color: row.change_type==="update" ? "#9a3412" : T.textMuted,
                  wordBreak:"break-word", lineHeight:1.4,
                  textDecoration: row.change_type==="update" ? "line-through" : "none",
                  opacity: row.change_type==="update" ? 0.7 : 1,
                }}>{String(row.catalog_value ?? "—")}</span>
                <span style={{
                  fontSize:"0.62rem", fontWeight:700, padding:"2px 7px",
                  borderRadius:4, textAlign:"center",
                  background:cb(row.change_type), color:cc(row.change_type),
                  border:`1px solid ${cx(row.change_type)}`,
                }}>
                  {row.change_type==="new" ? "NEW" : "UPD"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{
          padding:"48px 24px", border:`2px dashed ${T.border}`, borderRadius:10,
          textAlign:"center", color:T.textMuted, marginBottom:20,
          background:T.bgCard, fontSize:"0.84rem",
        }}>
          <div style={{ fontSize:"1.6rem", marginBottom:10 }}>⬡</div>
          <div style={{ fontWeight:600, color:T.textSecond, marginBottom:4 }}>No diff loaded yet</div>
          Click "Fetch Diff from Catalog" to compare ADRIVA values against the live catalog.
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button onClick={onBack} style={smallBtn(T.textMuted, true)}>← Back</button>
        <PrimaryBtn label="Next: Push to Catalog →" onClick={onNext} disabled={!allFetched} />
      </div>
    </div>
  );
}

// ─── STEP 4: PUSH ─────────────────────────────────────────────────────────────
function StepPush({ target, entities, diffData, pushState, pushResults, onPush, onBack, onClose }: any) {
  const isColl = target==="collibra";
  return (
    <div>
      <div style={sectionHead}>Push to {isColl ? "Collibra DGC" : "Alation"}</div>
      <div style={{
        padding:"10px 14px", borderRadius:8, marginBottom:20,
        background:T.brandLight, border:`1px solid ${T.brandMid}`,
        fontSize:"0.76rem", color:T.brand, lineHeight:1.6,
      }}>
        Only fields with lifecycle <strong>approved</strong> in GovernanceUI will be pushed.
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:28 }}>
        {entities.map((e: Entity) => {
          const d   = diffData[e.entityKey];
          const st  = pushState[e.entityKey] ?? "idle";
          const done= st==="done", err=st==="error";
          const res = pushResults[e.entityKey];
          return (
            <div key={e.entityKey} style={{
              padding:"16px 20px", borderRadius:10,
              background: done ? T.greenBg : err ? T.redBg : T.bgCard,
              border:`1px solid ${done ? T.greenBorder : err ? T.redBorder : T.border}`,
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)", transition:"all 0.25s",
            }}>
              <div style={{ display:"flex", alignItems:"center",
                justifyContent:"space-between", gap:16 }}>
                <div>
                  <div style={{ fontSize:"0.9rem", fontWeight:700, color:T.textPrimary,
                    fontFamily:"'Lora',Georgia,serif", marginBottom:8 }}>
                    {e.entityName}
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {d && (
                      <>
                        <Badge label={`${d.summary.new} new`} color={T.green} bg={T.greenBg} border={T.greenBorder} />
                        <Badge label={`${d.summary.update} updates`} color="#9a3412" bg={T.amberBg} border={T.amberBorder} />
                      </>
                    )}
                    {done && res && (
                      isColl ? (
                        <>
                          <Badge label={`${res.assets_upserted??0} assets`} color={T.brand} bg={T.brandLight} border={T.brandMid} />
                          <Badge label={`${res.attributes_set??0} attrs`}   color={T.brand} bg={T.brandLight} border={T.brandMid} />
                          <Badge label={`${res.relations_created??0} rels`} color={T.brand} bg={T.brandLight} border={T.brandMid} />
                        </>
                      ) : (
                        <>
                          <Badge label={`${res.columns_upserted??0} columns`}  color={T.brand} bg={T.brandLight} border={T.brandMid} />
                          <Badge label={`${res.articles_created??0} articles`} color={T.brand} bg={T.brandLight} border={T.brandMid} />
                          <Badge label={`${res.flags_created??0} flags`}       color={T.brand} bg={T.brandLight} border={T.brandMid} />
                        </>
                      )
                    )}
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                  {done && <span style={{ fontSize:"0.78rem", fontWeight:700, color:T.green }}>✓ Pushed</span>}
                  {err  && <span style={{ fontSize:"0.78rem", fontWeight:700, color:T.red }}>✗ Failed</span>}
                  <button onClick={() => onPush(e.entityKey)}
                    disabled={st==="pushing"||done} style={{
                    background: done ? T.greenBg : T.brand,
                    border:`1px solid ${done ? T.greenBorder : T.brand}`,
                    color: done ? T.green : "#fff",
                    borderRadius:7, padding:"7px 18px",
                    fontSize:"0.78rem", fontWeight:700,
                    cursor: st==="pushing"||done ? "default" : "pointer",
                    opacity: st==="pushing" ? 0.7 : 1,
                    boxShadow: !done ? "0 2px 8px rgba(44,74,124,0.18)" : "none",
                    transition:"all 0.15s",
                  }}>
                    {st==="pushing" ? "Pushing…" : done ? "Pushed ✓"
                      : `Push to ${isColl ? "Collibra" : "Alation"}`}
                  </button>
                </div>
              </div>
              {err && res?.errors?.length > 0 && (
                <div style={{
                  marginTop:10, padding:"8px 12px", borderRadius:6,
                  background:T.redBg, border:`1px solid ${T.redBorder}`,
                  fontSize:"0.72rem", color:T.red, lineHeight:1.55,
                }}>
                  {res.errors.slice(0, 3).map((e: string, i: number) => (
                    <div key={i}>• {e}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button onClick={onBack} style={smallBtn(T.textMuted, true)}>← Back</button>
        <PrimaryBtn label="Close" onClick={onClose} />
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function CatalogPromotionUI({
  entities, appNm, entityId,
  savedCreds = {}, savedMappings = {},
  onClose,
}: CatalogPromotionUIProps) {
  const [step,            setStep]           = useState(0);
  const [target,          setTarget]         = useState<"collibra"|"alation">("collibra");
  const [creds,           setCreds]          = useState<Creds>(savedCreds["collibra"] ?? { host:"", auth_type:"basic" });
  const [testState,       setTestState]      = useState<"idle"|"testing"|"ok"|"error">("idle");
  const [testMsg,         setTestMsg]        = useState("");
  const [mappings,        setMappings]       = useState<Record<string, EntityMapping>>(savedMappings["collibra"] ?? {});
  const [mapSave,         setMapSave]        = useState<Record<string, any>>({});
  const [diffData,        setDiffData]       = useState<Record<string, any>>({});
  const [diffLoad,        setDiffLoad]       = useState<Record<string, boolean>>({});
  const [pushState,       setPushState]      = useState<Record<string, any>>({});
  const [pushRes,         setPushRes]        = useState<Record<string, any>>({});

  // Collibra structure tree
  const [collibraStructure, setCollibraStructure] = useState<{ communities: CollibraCommunity[] } | null>(null);
  const [structureState,    setStructureState]    = useState<"idle"|"loading"|"ok"|"error">("idle");

  // Activity log
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [logOpen,    setLogOpen]    = useState(false);

  const addLog = useCallback((e: LogEntry) => {
    setLogEntries(prev => [...prev, e]);
  }, []);

  // ── Target switch ───────────────────────────────────────────────────────────
  const handleSetTarget = (t: "collibra"|"alation") => {
    setTarget(t);
    setCreds(savedCreds[t] ?? { host:"", auth_type:"basic" });
    setMappings(savedMappings[t] ?? {});
    setTestState("idle"); setTestMsg("");
    setDiffData({}); setPushState({});
    setCollibraStructure(null); setStructureState("idle");
    addLog(mkLog("info", "platform_switch", `Switched to ${t}`, t));
  };

  // ── Fetch Collibra structure (fires after successful test) ──────────────────
  const fetchStructure = useCallback(async (credsToUse: Creds) => {
    setStructureState("loading");
    addLog(mkLog("info", "FETCH_STRUCTURE", "Loading Collibra workspace structure…", "collibra"));
    const r = await postCatalogAction({
      action: "FETCH_STRUCTURE", app_nm: appNm, entity_id: entityId,
      target: "collibra", creds: credsToUse,
    });
    if (r.success && r.data?.communities) {
      setCollibraStructure({ communities: r.data.communities });
      setStructureState("ok");
      const n = r.data.communities.length;
      const nd = r.data.communities.reduce((s: number, c: any) => s + (c.domains?.length ?? 0), 0);
      addLog(mkLog("ok", "FETCH_STRUCTURE",
        `Loaded ${n} communities, ${nd} domains`, "collibra"));
    } else {
      setStructureState("error");
      addLog(mkLog("error", "FETCH_STRUCTURE",
        r.msg ?? "Failed to load workspace structure", "collibra"));
    }
  }, [appNm, entityId, addLog]);

  // ── Test connection ─────────────────────────────────────────────────────────
  const handleTest = useCallback(async () => {
    setTestState("testing");
    addLog(mkLog("info", "TEST_CONNECTION", `Testing ${target} @ ${creds.host}…`, target));
    const r = await postCatalogAction({
      action: "TEST_CONNECTION", app_nm: appNm, entity_id: entityId, target, creds,
    });
    if (r.success) {
      setTestState("ok"); setTestMsg("");
      addLog(mkLog("ok", "TEST_CONNECTION", `Connected — credentials saved`, target));
      // Auto-fetch Collibra structure
      if (target === "collibra") {
        fetchStructure(creds);
      }
    } else {
      setTestState("error"); setTestMsg(r.msg ?? "");
      addLog(mkLog("error", "TEST_CONNECTION", r.msg ?? "Connection failed", target));
    }
  }, [appNm, entityId, target, creds, addLog, fetchStructure]);

  // ── Save mapping ────────────────────────────────────────────────────────────
  const handleSaveMapping = useCallback(async (ek: string) => {
    setMapSave((p:any) => ({ ...p, [ek]:"saving" }));
    addLog(mkLog("info", "SAVE_MAPPING", `Saving mapping for ${ek}…`, target, ek));
    const m = mappings[ek] ?? (target==="collibra" ? COLLIBRA_DEFAULTS : ALATION_DEFAULTS);
    const r = await postCatalogAction({
      action: "SAVE_MAPPING", app_nm: appNm, entity_id: entityId,
      target, entity_key: ek, mapping: m,
    });
    setMapSave((p:any) => ({ ...p, [ek]: r.success ? "saved" : "error" }));
    addLog(mkLog(r.success ? "ok" : "error", "SAVE_MAPPING",
      r.success ? `Mapping saved for ${ek}` : r.msg ?? "Save failed",
      target, ek));
  }, [appNm, entityId, target, mappings, addLog]);

  // ── Fetch diff ──────────────────────────────────────────────────────────────
  const handleFetchDiff = useCallback(async (ek: string) => {
    setDiffLoad((p:any) => ({ ...p, [ek]:true }));
    addLog(mkLog("info", "FETCH_DIFF", `Comparing ${ek} against ${target} catalog…`, target, ek));
    const r = await postCatalogAction({
      action: "FETCH_DIFF", app_nm: appNm, entity_id: entityId, target, entity_key: ek,
    });
    setDiffLoad((p:any) => ({ ...p, [ek]:false }));
    if (r.success && r.data) {
      setDiffData((p:any) => ({ ...p, [ek]:r.data }));
      const s = r.data.summary;
      addLog(mkLog("ok", "FETCH_DIFF",
        `Diff complete — ${s.new} new, ${s.update} updates, ${s.no_change} unchanged`,
        target, ek));
    } else {
      addLog(mkLog("error", "FETCH_DIFF", r.msg ?? "Diff failed", target, ek));
    }
  }, [appNm, entityId, target, addLog]);

  // ── Push ────────────────────────────────────────────────────────────────────
  const handlePush = useCallback(async (ek: string) => {
    setPushState((p:any) => ({ ...p, [ek]:"pushing" }));
    addLog(mkLog("info", "PUSH", `Pushing approved fields for ${ek} → ${target}…`, target, ek));
    const r = await postCatalogAction({
      action: "PUSH", app_nm: appNm, entity_id: entityId, target, entity_key: ek,
    });
    setPushState((p:any) => ({ ...p, [ek]: r.success ? "done" : "error" }));
    setPushRes((p:any)   => ({ ...p, [ek]: r.data }));
    if (r.success) {
      const d = r.data ?? {};
      const pushed = r.fields_pushed ?? "?";
      addLog(mkLog("ok", "PUSH",
        `Push complete — ${pushed} fields` +
        (d.assets_upserted  != null ? `, ${d.assets_upserted} assets`    : "") +
        (d.attributes_set   != null ? `, ${d.attributes_set} attrs`      : "") +
        (d.columns_upserted != null ? `, ${d.columns_upserted} columns`  : "") +
        (d.errors?.length   ? `, ${d.errors.length} errors`              : ""),
        target, ek));
    } else {
      addLog(mkLog("error", "PUSH", r.msg ?? "Push failed", target, ek));
    }
  }, [appNm, entityId, target, addLog]);

  const stepTitles = [
    "Connect to Catalog",
    "Asset & Attribute Mapping",
    "Diff Preview",
    `Push to ${target==="collibra" ? "Collibra DGC" : "Alation"}`,
  ];

  return (
    <div style={{
      position:"fixed", inset:0,
      background:"rgba(26,35,50,0.45)",
      zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'Figtree','Segoe UI',sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800&family=Lora:wght@600;700&display=swap');
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:#c8d0dc;border-radius:99px;}
        input:focus,select:focus{outline:none!important;border-color:${T.borderFocus}!important;box-shadow:0 0 0 3px ${T.brand}18!important;}
        button:hover:not(:disabled){opacity:0.88;}
      `}</style>

      <div style={{
        background:T.bgCard,
        borderRadius:14,
        width:"min(980px,95vw)",
        maxHeight:"92vh",
        display:"flex", flexDirection:"column",
        overflow:"hidden",
        boxShadow:"0 20px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)",
      }}>
        {/* Header */}
        <div style={{
          padding:"20px 24px 16px", borderBottom:`1px solid ${T.border}`,
          background:T.bgCard,
          display:"flex", alignItems:"flex-start", justifyContent:"space-between",
          borderTop:`3px solid ${T.brand}`, flexShrink:0,
        }}>
          <div>
            <div style={{
              fontSize:"0.62rem", fontWeight:700, letterSpacing:"0.1em",
              textTransform:"uppercase", color:T.brand, marginBottom:6,
            }}>⬡ Catalog Promotion</div>
            <div style={{
              fontSize:"1rem", fontWeight:700, color:T.textPrimary,
              fontFamily:"'Lora',Georgia,serif",
            }}>{stepTitles[step]}</div>
          </div>
          <button onClick={onClose} style={{
            background:"none", border:"none", color:T.textMuted,
            fontSize:18, cursor:"pointer", padding:"2px 6px", lineHeight:1,
          }}>✕</button>
        </div>

        {/* Body — scrollable */}
        <div style={{ padding:"24px 28px", overflowY:"auto", flexGrow:1, background:T.bg }}>
          <StepBar step={step} />

          {step===0 && (
            <StepCredentials
              target={target} setTarget={handleSetTarget}
              creds={creds}   setCreds={setCreds}
              testState={testState} testMsg={testMsg}
              structureState={structureState}
              onTest={handleTest} onNext={() => setStep(1)}
            />
          )}
          {step===1 && (
            <StepMapping
              target={target} entities={entities}
              mappings={mappings} setMappings={setMappings}
              onSave={handleSaveMapping} saveState={mapSave}
              onNext={() => setStep(2)} onBack={() => setStep(0)}
              collibraStructure={collibraStructure}
              structureState={structureState}
            />
          )}
          {step===2 && (
            <StepDiff
              entities={entities}
              diffData={diffData} diffLoading={diffLoad}
              onFetch={handleFetchDiff}
              onNext={() => setStep(3)} onBack={() => setStep(1)}
            />
          )}
          {step===3 && (
            <StepPush
              target={target} entities={entities}
              diffData={diffData}
              pushState={pushState} pushResults={pushRes}
              onPush={handlePush} onBack={() => setStep(2)} onClose={onClose}
            />
          )}
        </div>

        {/* Activity Log — pinned to bottom */}
        <ActivityLog
          entries={logEntries}
          isOpen={logOpen}
          onToggle={() => setLogOpen(v => !v)}
        />
      </div>
    </div>
  );
}